import { shuffle, formatTime } from "../memory-match/gameUtils";

export { shuffle, formatTime };

export const DEFAULT_PUZZLE_IMAGE = "/assets/landing/landing-storybook.jpeg";

// MARK: - Adaptive difficulty (mirrors iOS PuzzleSkillRules)

export const PUZZLE_MIN_LEVEL = 3; // 3×3 = 9 pieces
export const PUZZLE_MAX_LEVEL = 6; // 6×6 = 36 pieces
export const SECONDS_PER_PIECE = 4.5; // par time per piece
export const LEVEL_UP_RATING = 0.8; // both of the last two solves
export const LEVEL_DOWN_RATING = 0.48; // this solve
const DIFFICULTY_STORAGE_KEY = "pixley_story_puzzle_difficulty";

export const clampLevel = (level) =>
  Math.min(PUZZLE_MAX_LEVEL, Math.max(PUZZLE_MIN_LEVEL, Math.round(level || PUZZLE_MIN_LEVEL)));

/** A 0…1 rating for one solve: how few mistakes (efficiency) and how quickly. */
export const ratingForSolve = (pieces, moves, seconds) => {
  const safePieces = Math.max(1, pieces);
  const efficiency = Math.min(1, safePieces / Math.max(moves, safePieces));
  const par = safePieces * SECONDS_PER_PIECE;
  const speed = Math.min(1, par / Math.max(seconds, 1));
  return 0.6 * efficiency + 0.4 * speed;
};

/**
 * Given the level just played and its rating history, decide the next level.
 * Returns { level, ratings, change } where change is -1/0/+1.
 */
export const evaluateLevel = (level, ratings, newRating) => {
  const nextRatings = [...(ratings || []), newRating].slice(-6);
  const clamped = clampLevel(level);
  const lastTwo = nextRatings.slice(-2);
  if (
    clamped < PUZZLE_MAX_LEVEL &&
    lastTwo.length === 2 &&
    lastTwo.every((r) => r >= LEVEL_UP_RATING)
  ) {
    return { level: clamped + 1, ratings: [], change: 1 };
  }
  const last = nextRatings[nextRatings.length - 1];
  if (clamped > PUZZLE_MIN_LEVEL && last != null && last <= LEVEL_DOWN_RATING) {
    return { level: clamped - 1, ratings: [], change: -1 };
  }
  return { level: clamped, ratings: nextRatings, change: 0 };
};

/** iOS scoring: max(80, total*70 - moves*3 - seconds*2). */
export const computeScore = (total, moves, seconds) =>
  Math.max(80, total * 70 - moves * 3 - seconds * 2);

/** Persist adaptive difficulty locally (per profile handled by the caller key). */
export const loadDifficulty = () => {
  try {
    const raw = localStorage.getItem(DIFFICULTY_STORAGE_KEY);
    if (!raw) return { level: PUZZLE_MIN_LEVEL, ratings: [] };
    const parsed = JSON.parse(raw);
    return {
      level: clampLevel(parsed?.level ?? PUZZLE_MIN_LEVEL),
      ratings: Array.isArray(parsed?.ratings) ? parsed.ratings : [],
    };
  } catch {
    return { level: PUZZLE_MIN_LEVEL, ratings: [] };
  }
};

export const saveDifficulty = (level, ratings) => {
  try {
    localStorage.setItem(
      DIFFICULTY_STORAGE_KEY,
      JSON.stringify({ level: clampLevel(level), ratings: ratings || [] })
    );
  } catch {
    // Ignore storage issues.
  }
};

// MARK: - Image source

/**
 * Build the pool of puzzle pictures from the child's OWN character art in the
 * games payload (mirrors iOS `puzzleAssets`): character/bubble-pop art first,
 * then the child's story thumbnails, finally the bundled default so the puzzle
 * is always playable.
 */
export const resolvePuzzleImages = (gamesPayload) => {
  const payload = gamesPayload || {};
  const urls = [];
  const push = (url) => {
    if (url && typeof url === "string" && !urls.includes(url)) urls.push(url);
  };

  (payload.characters || []).forEach((c) => push(c?.animated_image_url || c?.image_url));
  (payload.bubble_pop?.targets || []).forEach((a) => push(a?.image_url));
  (payload.memory_match?.assets || []).forEach((a) => push(a?.image_url));
  push(payload.story?.thumbnail_url);

  if (!urls.length) push(DEFAULT_PUZZLE_IMAGE);
  return urls;
};

/** First available puzzle picture (used as a goal-preview fallback). */
export const resolvePuzzleImage = (gamesPayload) => resolvePuzzleImages(gamesPayload)[0];

// MARK: - Compositing (aspect-fill character over a vivid gradient square)

/** iOS gradient colours (green → blue → violet → peach), diagonal. */
export const GRADIENT_STOPS = [
  { at: 0, color: "#5CD1A8" },
  { at: 0.35, color: "#66A8FF" },
  { at: 0.68, color: "#B885FF" },
  { at: 1, color: "#FFA875" },
];

export const gradientCss = (angle = 135) =>
  `linear-gradient(${angle}deg, ${GRADIENT_STOPS.map((s) => `${s.color} ${Math.round(s.at * 100)}%`).join(", ")})`;

/**
 * Composite the character image aspect-FILL over the 4-colour gradient onto a
 * `side`×`side` square canvas and return a data URL. Falls back to the raw URL
 * if the image can't be loaded cross-origin cleanly (canvas would taint), so
 * rendering still works — pieces draw a gradient layer behind the picture to
 * guarantee no tile is ever blank regardless of which path is taken.
 */
export const composePuzzleImage = (imageUrl, side = 1200) =>
  new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve({ boardImage: imageUrl || DEFAULT_PUZZLE_IMAGE, composited: false });
      return;
    }

    const paintGradient = (ctx) => {
      const g = ctx.createLinearGradient(0, 0, side, side);
      GRADIENT_STOPS.forEach((s) => g.addColorStop(s.at, s.color));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, side, side);
    };

    const makeCanvas = () => {
      const canvas = document.createElement("canvas");
      canvas.width = side;
      canvas.height = side;
      return canvas;
    };

    // No image → bare gradient square (still colourful, never blank).
    if (!imageUrl) {
      const canvas = makeCanvas();
      const ctx = canvas.getContext("2d");
      paintGradient(ctx);
      try {
        resolve({ boardImage: canvas.toDataURL("image/jpeg", 0.92), composited: true });
      } catch {
        resolve({ boardImage: DEFAULT_PUZZLE_IMAGE, composited: false });
      }
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = makeCanvas();
        const ctx = canvas.getContext("2d");
        paintGradient(ctx);
        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;
        if (iw > 0 && ih > 0) {
          const scale = Math.max(side / iw, side / ih); // aspect-FILL
          const w = iw * scale;
          const h = ih * scale;
          ctx.drawImage(img, (side - w) / 2, (side - h) / 2, w, h);
        }
        resolve({ boardImage: canvas.toDataURL("image/jpeg", 0.92), composited: true });
      } catch {
        // Tainted canvas (no CORS headers) → fall back to the raw URL; pieces
        // paint their own gradient backdrop so transparent/cropped areas stay
        // colourful.
        resolve({ boardImage: imageUrl, composited: false });
      }
    };
    img.onerror = () => resolve({ boardImage: DEFAULT_PUZZLE_IMAGE, composited: false });
    img.src = imageUrl;
  });

// MARK: - Board model (integer pieces; a piece's home slot is its own index)

export const buildInitialState = (total) => ({
  slots: Array.from({ length: total }, () => null),
  bank: shuffle(Array.from({ length: total }, (_, i) => i)),
});

/**
 * Move `piece` (from the bank or a slot) onto a target slot, or back to the
 * bank when `target` is null. Mistakes are allowed; occupied slots swap. `from`
 * is `{ kind: "bank" }` or `{ kind: "slot", index }`. Returns a fresh state.
 */
export const applyDrop = (state, piece, from, target) => {
  const slots = [...state.slots];
  let bank = [...state.bank];

  if (from?.kind === "bank") {
    bank = bank.filter((p) => p !== piece);
  } else if (from?.kind === "slot" && slots[from.index] === piece) {
    slots[from.index] = null;
  }

  const validTarget = target != null && target >= 0 && target < slots.length;
  if (!validTarget) {
    if (!bank.includes(piece)) bank.push(piece);
    return { slots, bank };
  }

  const displaced = slots[target];
  if (displaced != null && displaced !== piece) {
    slots[target] = piece;
    if (from?.kind === "slot") {
      slots[from.index] = displaced;
    } else {
      bank.push(displaced);
    }
  } else {
    slots[target] = piece;
  }
  return { slots, bank };
};

export const isSolved = (slots) =>
  Array.isArray(slots) && slots.length > 0 && slots.every((v, i) => v === i);

export const placedCount = (slots) => slots.reduce((n, v) => n + (v == null ? 0 : 1), 0);

// MARK: - Jigsaw geometry (uniform interlocking pattern, mirrors iOS JigsawEdges)

export const TAB_RATIO = 0.3;

/**
 * Uniform edges: every interior piece bites IN on top/left and bulges OUT on
 * right/bottom, so a tab always meets a blank at any seam regardless of which
 * piece lands where. Outer edges are flat for clean borders.
 */
export const jigsawEdges = (index, grid) => {
  const r = Math.floor(index / grid);
  const c = index % grid;
  return {
    top: r > 0 ? "blank" : "flat",
    right: c < grid - 1 ? "tab" : "flat",
    bottom: r < grid - 1 ? "tab" : "flat",
    left: c > 0 ? "blank" : "flat",
  };
};

/**
 * SVG path `d` for one jigsaw piece (mirrors iOS JigsawShape): the `tile` sits
 * inset by `tab` inside a full-size box; tabs bulge into that margin, blanks
 * bite inward. A classic mushroom knob (shoulders → neck → round bulb).
 */
export const jigsawPath = (edges, tile, tab) => {
  const i = tab;
  const cmds = [];
  const move = (p) => cmds.push(`M ${p.x} ${p.y}`);
  const line = (p) => cmds.push(`L ${p.x} ${p.y}`);
  const curve = (c1, c2, p) => cmds.push(`C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${p.x} ${p.y}`);

  const edge = (start, dir, out, type) => {
    const end = { x: start.x + dir.dx * tile, y: start.y + dir.dy * tile };
    if (type === "flat") {
      line(end);
      return;
    }
    const s = type === "tab" ? 1 : -1;
    const pt = (a, pp) => {
      const along = a * tile;
      const perp = pp * tile * s;
      return {
        x: start.x + dir.dx * along + out.dx * perp,
        y: start.y + dir.dy * along + out.dy * perp,
      };
    };
    line(pt(0.37, 0));
    curve(pt(0.47, 0.01), pt(0.25, 0.11), pt(0.35, 0.17));
    curve(pt(0.3, 0.285), pt(0.7, 0.285), pt(0.65, 0.17));
    curve(pt(0.75, 0.11), pt(0.53, 0.01), pt(0.63, 0));
    line(end);
  };

  move({ x: i, y: i });
  edge({ x: i, y: i }, { dx: 1, dy: 0 }, { dx: 0, dy: -1 }, edges.top);
  edge({ x: i + tile, y: i }, { dx: 0, dy: 1 }, { dx: 1, dy: 0 }, edges.right);
  edge({ x: i + tile, y: i + tile }, { dx: -1, dy: 0 }, { dx: 0, dy: 1 }, edges.bottom);
  edge({ x: i, y: i + tile }, { dx: 0, dy: -1 }, { dx: -1, dy: 0 }, edges.left);
  cmds.push("Z");
  return cmds.join(" ");
};
